/**
 * AuditTrailSecurityComplianceLogPage — read-only audit log browser.
 * Shows an immutable append-only audit trail with filters on actor, severity, and date range.
 * GREEN-only accent (no emerald/blue/orange/purple/dark/emoji).
 */
import { useMemo, useState } from "react";
import { useAuditTrailSecurityComplianceLogList } from "./hooks";
import {
	ALLOWED_SEVERITY,
	filterRows,
	formatTimestamp,
	parseDateInput,
	type AuditSeverity,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const LABEL = "text-xs font-medium text-zinc-600";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50";

const SEV_PILL: Record<AuditSeverity, string> = {
	info: "bg-zinc-100 text-zinc-700 border-zinc-200",
	warning: "bg-green-50 text-green-800 border-green-200",
	critical: "bg-green-700 text-white border-green-800",
};

export function AuditTrailSecurityComplianceLogPage() {
	const [actorQuery, setActorQuery] = useState("");
	const [severity, setSeverity] = useState<AuditSeverity | "">("");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");

	// Fetch broadly (no filter) and filter client-side so UI state stays snappy
	// and parseDateInput range semantics are uniform.
	const listQuery = useAuditTrailSecurityComplianceLogList({ limit: 500 });

	const rows = listQuery.data?.items ?? [];
	const filtered = useMemo(
		() =>
			filterRows(rows, {
				actor_id: actorQuery,
				severity,
				from: parseDateInput(from, false),
				to: parseDateInput(to, true),
			}),
		[rows, actorQuery, severity, from, to],
	);

	const reset = () => {
		setActorQuery("");
		setSeverity("");
		setFrom("");
		setTo("");
	};

	return (
		<main
			data-testid="audit-trail-security-compliance-log-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					audit-trail-security-compliance-log
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Audit Trail
				</h1>
				<p className="text-sm text-zinc-600">
					Immutable append-only record of security and compliance actions.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Audit trail filters"
				data-testid="audit-trail-security-compliance-log-filters"
			>
				<h2 className="text-base font-semibold text-zinc-900">Filters</h2>
				<div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-5">
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label className={LABEL} htmlFor="audit-actor">
							Actor
						</label>
						<input
							id="audit-actor"
							type="text"
							className={INPUT}
							placeholder="actor id contains..."
							value={actorQuery}
							onChange={(e) => setActorQuery(e.currentTarget.value)}
							data-testid="audit-trail-security-compliance-log-filter-actor"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="audit-severity">
							Severity
						</label>
						<select
							id="audit-severity"
							className={INPUT}
							value={severity}
							onChange={(e) =>
								setSeverity(e.currentTarget.value as AuditSeverity | "")
							}
							data-testid="audit-trail-security-compliance-log-filter-severity"
						>
							<option value="">All</option>
							{ALLOWED_SEVERITY.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="audit-from">
							From
						</label>
						<input
							id="audit-from"
							type="date"
							className={INPUT}
							value={from}
							onChange={(e) => setFrom(e.currentTarget.value)}
							data-testid="audit-trail-security-compliance-log-filter-from"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="audit-to">
							To
						</label>
						<input
							id="audit-to"
							type="date"
							className={INPUT}
							value={to}
							onChange={(e) => setTo(e.currentTarget.value)}
							data-testid="audit-trail-security-compliance-log-filter-to"
						/>
					</div>
				</div>
				<div className="mt-4 flex items-center gap-3">
					<button
						type="button"
						className={BTN_GHOST}
						onClick={reset}
						data-testid="audit-trail-security-compliance-log-filter-reset"
					>
						Reset
					</button>
					<button
						type="button"
						className={BTN}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
						data-testid="audit-trail-security-compliance-log-refresh"
					>
						{listQuery.isFetching ? "Refreshing..." : "Refresh"}
					</button>
					<span
						className="text-xs text-zinc-500"
						data-testid="audit-trail-security-compliance-log-count"
					>
						{filtered.length} of {rows.length} events
					</span>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Audit trail events"
			>
				<h2 className="text-base font-semibold text-zinc-900">Events</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="audit-trail-security-compliance-log-loading"
					>
						Loading...
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-700"
						role="alert"
						data-testid="audit-trail-security-compliance-log-error"
					>
						{listQuery.error.message}
					</p>
				) : filtered.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="audit-trail-security-compliance-log-empty"
					>
						No audit events match the current filters.
					</p>
				) : (
					<div
						className="mt-3 overflow-x-auto"
						data-testid="audit-trail-security-compliance-log-list"
					>
						<table className="min-w-full border-collapse text-left text-xs">
							<thead>
								<tr className="border-b border-zinc-200 text-zinc-600">
									<th className="px-3 py-2 font-medium">Timestamp</th>
									<th className="px-3 py-2 font-medium">Actor</th>
									<th className="px-3 py-2 font-medium">Action</th>
									<th className="px-3 py-2 font-medium">Resource</th>
									<th className="px-3 py-2 font-medium">Tenant</th>
									<th className="px-3 py-2 font-medium">Severity</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((row) => (
									<tr
										key={row.id}
										className="border-b border-zinc-100 text-zinc-800 hover:bg-green-50/40"
										data-testid={`audit-trail-security-compliance-log-row-${row.id}`}
									>
										<td className="px-3 py-2 font-mono">
											{formatTimestamp(row.created_at)}
										</td>
										<td className="px-3 py-2 font-mono">{row.actor_id}</td>
										<td className="px-3 py-2">{row.action}</td>
										<td className="px-3 py-2">
											<span className="font-mono text-zinc-700">
												{row.resource_type}
											</span>
											{row.resource_id ? (
												<span className="ml-1 font-mono text-zinc-500">
													:{row.resource_id}
												</span>
											) : null}
										</td>
										<td className="px-3 py-2 font-mono text-zinc-600">
											{row.tenant_id}
										</td>
										<td className="px-3 py-2">
											<span
												className={`inline-flex items-center rounded-[4px] border px-2 py-0.5 text-[11px] font-medium ${SEV_PILL[row.severity]}`}
											>
												{row.severity}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AuditTrailSecurityComplianceLogPage;
